import { type FC, type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search, X } from 'lucide-react';

import { cn } from '@/shared/libs';

import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface MultiSelectFilterOption {
  icon?: ReactNode;
  label: string;
  value: string;
}

export interface MultiSelectFilterProps {
  label: string;
  onChange: (value: string[]) => void;
  options: readonly MultiSelectFilterOption[];
  searchable?: number;
  selected: readonly string[];
}

export const MultiSelectFilter: FC<MultiSelectFilterProps> = ({
  label,
  onChange,
  options,
  searchable = 8,
  selected,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    if (!needle) {
      return options;
    }

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.value.toLowerCase().includes(needle),
    );
  }, [options, query]);
  const allSelected = options.length > 0 && selected.length === options.length;
  const summary =
    selected.length === 0
      ? label
      : allSelected
        ? t('filters.multi.summaryAll', { label })
        : `${label}: ${selected
            .map(
              (value) =>
                options.find((option) => option.value === value)?.label ??
                value,
            )
            .join(', ')}`;

  const toggle = (value: string): void => {
    onChange(
      selectedSet.has(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);

        if (!next) {
          setQuery('');
        }
      }}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'inline-flex h-9 max-w-[220px] cursor-pointer items-center gap-1.5 rounded-xl border px-3 text-sm font-medium',
            selected.length > 0
              ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-300'
              : 'border-border/60 bg-background/60 text-foreground/80 hover:border-indigo-500/30',
          )}
          type="button">
          <span className="truncate">{summary}</span>
          {selected.length > 0 && (
            <span
              className="inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-rose-500/15 hover:text-rose-500"
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                onChange([]);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange([]);
                }
              }}>
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-0"
        onOpenAutoFocus={(event) => event.preventDefault()}>
        {options.length >= searchable && (
          <div className="relative border-b border-border/40 p-2">
            <Search className="pointer-events-none absolute top-1/2 left-4 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              className="w-full rounded-lg border border-border/40 bg-background/60 py-1.5 pr-2 pl-8 text-xs outline-none focus:ring-2 focus:ring-indigo-500/40"
              placeholder={t('filters.multi.search')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        )}
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              {t('filters.multi.empty')}
            </p>
          )}
          {filtered.map((option) => {
            const checked = selectedSet.has(option.value);

            return (
              <button
                key={option.value}
                className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-foreground/5"
                type="button"
                onClick={() => toggle(option.value)}>
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    checked
                      ? 'border-indigo-500 bg-indigo-500 text-white'
                      : 'border-border bg-background',
                  )}>
                  {checked && <Check className="h-3 w-3" />}
                </span>
                {option.icon}
                <span className="truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border/40 bg-foreground/2 px-2 py-2">
          <button
            className="cursor-pointer rounded-lg bg-foreground/5 px-2.5 py-1.5 text-xs font-medium hover:bg-foreground/10"
            type="button"
            onClick={() =>
              onChange(allSelected ? [] : options.map((option) => option.value))
            }>
            {t('filters.multi.all', { count: options.length })}
          </button>
          {selected.length > 0 && (
            <button
              className="cursor-pointer rounded-lg px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-500/10 dark:text-rose-400"
              type="button"
              onClick={() => onChange([])}>
              {t('filters.multi.clear')}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

MultiSelectFilter.displayName = 'MultiSelectFilter';
